import {
  addMoney,
  type LocalDate,
  type Money,
  fromApiMoney,
  parseLocalDate,
  type Ratio,
  ratio,
} from '@taler/domain'
import type { OperationQuery, Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, ledgerKey } from '../http/keys.ts'
import { projectQuery, type QueryProjection, queryFingerprint } from '../http/query.ts'

export type ReportBucket = OperationQuery<'reporting_pivot'>['bucket']

export interface NetWorthPoint {
  readonly date: LocalDate
  readonly cash: Money
  readonly netWorth: Money
}

export interface NetWorthSeries {
  readonly points: readonly NetWorthPoint[]
  readonly resolutionDays: number
}

export interface WeeklySpending {
  readonly weekStart: LocalDate
  readonly amount: Money
}

export interface CashFlow {
  readonly grow: Money
  readonly owe: Money
  readonly live: Money
  readonly surplus: Money
}

export interface Dashboard {
  readonly asOf: LocalDate
  readonly displayCurrency: Schemas['DashboardResponse']['displayCurrency']
  readonly cash: Money
  readonly netWorth: Money
  /** Money lent to contacts and other ledgers, net of forgiveness - next to net worth, never inside it. */
  readonly totalLent: Money
  /** Money owed to contacts and other ledgers, as a magnitude - next to net worth, never inside it. */
  readonly totalBorrowed: Money
  readonly incomeMonthToDate: Money
  readonly expenseMonthToDate: Money
  readonly netWorthSeries: readonly NetWorthPoint[]
  readonly netWorthSeriesResolutionDays: number
  readonly weeklySpending: readonly WeeklySpending[]
  readonly cashFlow: CashFlow
}

export type DashboardQuery = OperationQuery<'reporting_dashboard'>
export type NetWorthSeriesQuery = OperationQuery<'reporting_netWorthSeries'>

const DASHBOARD_PROJECTION: QueryProjection<DashboardQuery> = {
  asOf: (value) => value,
  displayCurrency: (value) => value,
  weekOffset: (value) => String(value),
}

const NET_WORTH_SERIES_PROJECTION: QueryProjection<NetWorthSeriesQuery> = {
  from: (value) => value,
  until: (value) => value,
  displayCurrency: (value) => value,
  maxPoints: (value) => String(value),
}

export interface NetWorthAt {
  readonly date: LocalDate
  readonly netWorth: Money
}

export type NetWorthQuery = OperationQuery<'reporting_netWorth'>

const NET_WORTH_PROJECTION: QueryProjection<NetWorthQuery> = {
  date: (value) => value,
  displayCurrency: (value) => value,
}

export interface ReportCell {
  readonly amount: Money
  readonly entryCount: number
  readonly isLowVolume: boolean
}

export interface ReportPeriod {
  readonly periodStart: LocalDate
  readonly periodTotal: Money
  readonly byAccount: Readonly<Record<string, ReportCell>>
}

export interface Pivot {
  readonly periods: readonly ReportPeriod[]
}

export type PivotQuery = OperationQuery<'reporting_pivot'>

const PIVOT_PROJECTION: QueryProjection<PivotQuery> = {
  class: (value) => value,
  bucket: (value) => value,
  from: (value) => value,
  until: (value) => value,
  displayCurrency: (value) => value,
}

export interface RollupRow {
  readonly accountId: string
  readonly parentId: string | undefined
  readonly ownBalance: Money
  readonly rolledUpBalance: Money
}

export interface ParentRollupPeriod {
  readonly periodStart: LocalDate
  readonly otherByParent: ReadonlyMap<string, Money>
  readonly rows: readonly RollupRow[]
}

export interface ParentRollup {
  readonly periods: readonly ParentRollupPeriod[]
}

export interface RollupSnapshot {
  readonly otherByParent: ReadonlyMap<string, Money>
  readonly rows: readonly RollupRow[]
}

/**
 * `reporting_parentRollup` now always buckets by period; a caller that wants one
 * snapshot "as of `until`, since the ledger began" (rather than a trend) collapses every
 * returned period back into one by adding same-currency figures across periods. Exact
 * whenever the account's own currency already equals `displayCurrency` (no conversion, so
 * no per-period rate to drift between periods); for a converted account this carries the
 * same period-boundary rate-timing difference already accepted between other reports in
 * this app (see AGENTS.md's `RPT-6` and the parent-rollup "Egyéb" precedents).
 */
export function collapseParentRollupPeriods(rollup: ParentRollup): RollupSnapshot {
  const ownBalance = new Map<string, Money>()
  const rolledUpBalance = new Map<string, Money>()
  const parentOf = new Map<string, string | undefined>()
  const otherByParent = new Map<string, Money>()

  const accumulate = (into: Map<string, Money>, key: string, amount: Money): void => {
    const running = into.get(key)
    into.set(key, running === undefined ? amount : addMoney(running, amount))
  }

  for (const period of rollup.periods) {
    for (const row of period.rows) {
      parentOf.set(row.accountId, row.parentId)
      accumulate(ownBalance, row.accountId, row.ownBalance)
      accumulate(rolledUpBalance, row.accountId, row.rolledUpBalance)
    }
    for (const [parentId, amount] of period.otherByParent) accumulate(otherByParent, parentId, amount)
  }

  return {
    otherByParent,
    rows: [...parentOf.entries()].map(([accountId, parentId]) => ({
      accountId,
      parentId,
      ownBalance: ownBalance.get(accountId)!,
      rolledUpBalance: rolledUpBalance.get(accountId)!,
    })),
  }
}

export type ParentRollupQuery = OperationQuery<'reporting_parentRollup'>

const PARENT_ROLLUP_PROJECTION: QueryProjection<ParentRollupQuery> = {
  class: (value) => value,
  bucket: (value) => value,
  from: (value) => value,
  until: (value) => value,
  displayCurrency: (value) => value,
}

export interface CashFlowTotals {
  readonly income: Money
  readonly expense: Money
  readonly net: Money
  /** Undefined when income is zero or negative. */
  readonly savingsRate: Ratio | undefined
}

export interface CashFlowPeriod {
  readonly periodStart: LocalDate
  readonly income: Money
  readonly expense: Money
  readonly net: Money
}

export interface CashFlowReport extends CashFlowTotals {
  /** Newest first, sparse: a bucket with no postings is absent. */
  readonly periods: readonly CashFlowPeriod[]
  readonly previousPeriod: CashFlowTotals
}

export type CashFlowQuery = OperationQuery<'reporting_cashFlow'>

const CASH_FLOW_PROJECTION: QueryProjection<CashFlowQuery> = {
  from: (value) => value,
  until: (value) => value,
  bucket: (value) => value,
  displayCurrency: (value) => value,
}

export type ExpenseBreakdownQuery = OperationQuery<'reporting_expenseBreakdown'>
export type ExpenseGroupBy = ExpenseBreakdownQuery['groupBy']

export type ExpenseGroup =
  | { readonly kind: 'category'; readonly accountId: string }
  | { readonly kind: 'payee'; readonly merchantId: string | undefined; readonly name: string | undefined }
  | { readonly kind: 'tag'; readonly tag: string }
  | { readonly kind: 'untagged' }

export interface ExpenseBreakdownItem {
  readonly group: ExpenseGroup
  readonly amount: Money
  readonly entryCount: number
  /** `amount` over the total; undefined when the total is zero. */
  readonly share: Ratio | undefined
}

/** Items are largest first; a `TAG` breakdown can sum above `total` since an entry counts under each tag. */
export interface ExpenseBreakdown {
  readonly displayCurrency: ExpenseBreakdownQuery['displayCurrency']
  readonly total: Money
  readonly previousTotal: Money
  readonly items: readonly ExpenseBreakdownItem[]
}

const EXPENSE_BREAKDOWN_PROJECTION: QueryProjection<ExpenseBreakdownQuery> = {
  from: (value) => value,
  until: (value) => value,
  bucket: (value) => value,
  displayCurrency: (value) => value,
  groupBy: (value) => value,
}

export type LargestTransactionsQuery = OperationQuery<'reporting_largestTransactions'>

export interface LargestTransaction {
  readonly entryId: string
  readonly date: LocalDate
  readonly payee: string | undefined
  readonly tags: readonly string[]
  readonly categoryAccountId: string | undefined
  readonly accountId: string | undefined
  /** Money moved through the entry's money accounts, signed: negative is money out. */
  readonly amount: Money
}

const LARGEST_TRANSACTIONS_PROJECTION: QueryProjection<LargestTransactionsQuery> = {
  from: (value) => value,
  until: (value) => value,
  displayCurrency: (value) => value,
  limit: (value) => String(value),
}

export type AccountFlowSummariesQuery = OperationQuery<'reporting_accountFlowSummaries'>

export interface AccountFlowSummary {
  readonly accountId: string
  readonly count: number
  readonly inflow: Money
  readonly outflow: Money
  readonly transfersIn: Money
  readonly transfersOut: Money
  readonly net: Money
  readonly adjustments: Money
}

export interface AccountFlowSummaries {
  readonly displayCurrency: AccountFlowSummariesQuery['displayCurrency']
  readonly accounts: readonly AccountFlowSummary[]
}

const ACCOUNT_FLOW_SUMMARIES_PROJECTION: QueryProjection<AccountFlowSummariesQuery> = {
  from: (value) => value,
  until: (value) => value,
  displayCurrency: (value) => value,
}

/** Exactly one of `contactId`/`otherLedgerId` names who a row is about. */
export type LentToTarget =
  | { readonly kind: 'contact'; readonly contactId: string }
  | { readonly kind: 'ledger'; readonly otherLedgerId: string }

/** One currency of one target's lending history - owed/repaid/forgiven are already converted
 * to `LentTo.displayCurrency`. */
export interface LentToItem {
  readonly target: LentToTarget
  readonly name: string
  readonly owed: Money
  readonly repaid: Money
  readonly forgiven: Money
}

export interface LentTo {
  readonly displayCurrency: LentToQuery['displayCurrency']
  readonly items: readonly LentToItem[]
}

export type LentToQuery = OperationQuery<'reporting_lentTo'>

const LENT_TO_PROJECTION: QueryProjection<LentToQuery> = {
  displayCurrency: (value) => value,
}

export const reportingKeys = {
  dashboard: (ledgerId: string, query: DashboardQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'reporting',
      'dashboard',
      queryFingerprint(projectQuery(query, DASHBOARD_PROJECTION)),
    ),
  netWorthSeries: (ledgerId: string, query: NetWorthSeriesQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'reporting',
      'net-worth-series',
      queryFingerprint(projectQuery(query, NET_WORTH_SERIES_PROJECTION)),
    ),
  netWorth: (ledgerId: string, query: NetWorthQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'reporting',
      'net-worth',
      queryFingerprint(projectQuery(query, NET_WORTH_PROJECTION)),
    ),
  pivot: (ledgerId: string, query: PivotQuery): CacheKey =>
    ledgerKey(ledgerId, 'reporting', 'pivot', queryFingerprint(projectQuery(query, PIVOT_PROJECTION))),
  parentRollup: (ledgerId: string, query: ParentRollupQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'reporting',
      'parent-rollup',
      queryFingerprint(projectQuery(query, PARENT_ROLLUP_PROJECTION)),
    ),
  cashFlow: (ledgerId: string, query: CashFlowQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'reporting',
      'cash-flow',
      queryFingerprint(projectQuery(query, CASH_FLOW_PROJECTION)),
    ),
  expenseBreakdown: (ledgerId: string, query: ExpenseBreakdownQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'reporting',
      'expense-breakdown',
      queryFingerprint(projectQuery(query, EXPENSE_BREAKDOWN_PROJECTION)),
    ),
  largestTransactions: (ledgerId: string, query: LargestTransactionsQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'reporting',
      'largest-transactions',
      queryFingerprint(projectQuery(query, LARGEST_TRANSACTIONS_PROJECTION)),
    ),
  accountFlowSummaries: (ledgerId: string, query: AccountFlowSummariesQuery): CacheKey =>
    ledgerKey(
      ledgerId,
      'reporting',
      'account-flow-summaries',
      queryFingerprint(projectQuery(query, ACCOUNT_FLOW_SUMMARIES_PROJECTION)),
    ),
  lentTo: (ledgerId: string, query: LentToQuery): CacheKey =>
    ledgerKey(ledgerId, 'reporting', 'lent-to', queryFingerprint(projectQuery(query, LENT_TO_PROJECTION))),
} as const

export interface ReportingRepository {
  dashboard(ledgerId: string, query: DashboardQuery, signal?: AbortSignal): Promise<Dashboard>
  netWorthSeries(ledgerId: string, query: NetWorthSeriesQuery, signal?: AbortSignal): Promise<NetWorthSeries>
  netWorth(ledgerId: string, query: NetWorthQuery, signal?: AbortSignal): Promise<NetWorthAt>
  pivot(ledgerId: string, query: PivotQuery, signal?: AbortSignal): Promise<Pivot>
  parentRollup(ledgerId: string, query: ParentRollupQuery, signal?: AbortSignal): Promise<ParentRollup>
  cashFlow(ledgerId: string, query: CashFlowQuery, signal?: AbortSignal): Promise<CashFlowReport>
  expenseBreakdown(
    ledgerId: string,
    query: ExpenseBreakdownQuery,
    signal?: AbortSignal,
  ): Promise<ExpenseBreakdown>
  largestTransactions(
    ledgerId: string,
    query: LargestTransactionsQuery,
    signal?: AbortSignal,
  ): Promise<readonly LargestTransaction[]>
  accountFlowSummaries(
    ledgerId: string,
    query: AccountFlowSummariesQuery,
    signal?: AbortSignal,
  ): Promise<AccountFlowSummaries>
  lentTo(ledgerId: string, query: LentToQuery, signal?: AbortSignal): Promise<LentTo>
}

export function createReportingRepository(client: TalerClient): ReportingRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/reporting`

  return {
    async dashboard(ledgerId, query, signal) {
      const response = await client.call<Schemas['DashboardResponse']>({
        method: 'GET',
        path: `${base(ledgerId)}/dashboard`,
        query: projectQuery(query, DASHBOARD_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return toDashboard(response)
    },

    async netWorthSeries(ledgerId, query, signal) {
      const response = await client.call<Schemas['NetWorthSeriesResponse']>({
        method: 'GET',
        path: `${base(ledgerId)}/net-worth-series`,
        query: projectQuery(query, NET_WORTH_SERIES_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return {
        points: response.points.map(toNetWorthPoint),
        resolutionDays: response.resolutionDays,
      }
    },

    async netWorth(ledgerId, query, signal) {
      const response = await client.call<Schemas['NetWorthResponse']>({
        method: 'GET',
        path: `${base(ledgerId)}/net-worth`,
        query: projectQuery(query, NET_WORTH_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return { date: parseLocalDate(response.date), netWorth: fromApiMoney(response.netWorth) }
    },

    async pivot(ledgerId, query, signal) {
      const response = await client.call<Schemas['ReportsPivotResponse']>({
        method: 'GET',
        path: `${base(ledgerId)}/pivot`,
        query: projectQuery(query, PIVOT_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return { periods: response.periods.map(toReportPeriod) }
    },

    async parentRollup(ledgerId, query, signal) {
      const response = await client.call<Schemas['ParentRollupResponse']>({
        method: 'GET',
        path: `${base(ledgerId)}/parent-rollup`,
        query: projectQuery(query, PARENT_ROLLUP_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return { periods: response.periods.map(toParentRollupPeriod) }
    },

    async cashFlow(ledgerId, query, signal) {
      const response = await client.call<Schemas['CashFlowReportResponse']>({
        method: 'GET',
        path: `${base(ledgerId)}/cash-flow`,
        query: projectQuery(query, CASH_FLOW_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return {
        ...toCashFlowTotals(response),
        periods: response.periods.map((period) => ({
          periodStart: parseLocalDate(period.periodStart),
          income: fromApiMoney(period.income),
          expense: fromApiMoney(period.expense),
          net: fromApiMoney(period.net),
        })),
        previousPeriod: toCashFlowTotals(response.previousPeriod),
      }
    },

    async expenseBreakdown(ledgerId, query, signal) {
      const response = await client.call<Schemas['ExpenseBreakdownResponse']>({
        method: 'GET',
        path: `${base(ledgerId)}/expense-breakdown`,
        query: projectQuery(query, EXPENSE_BREAKDOWN_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return {
        displayCurrency: response.displayCurrency,
        total: fromApiMoney(response.total),
        previousTotal: fromApiMoney(response.previousTotal),
        items: response.items.map((item) => toExpenseBreakdownItem(item, query.groupBy)),
      }
    },

    async largestTransactions(ledgerId, query, signal) {
      const response = await client.call<Schemas['LargestTransactionResponse'][]>({
        method: 'GET',
        path: `${base(ledgerId)}/largest-transactions`,
        query: projectQuery(query, LARGEST_TRANSACTIONS_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return response.map((row) => ({
        entryId: row.entryId,
        date: parseLocalDate(row.date),
        payee: row.payee,
        tags: row.tags,
        categoryAccountId: row.categoryAccountId,
        accountId: row.accountId,
        amount: fromApiMoney(row.amount),
      }))
    },

    async accountFlowSummaries(ledgerId, query, signal) {
      const response = await client.call<Schemas['AccountFlowSummariesResponse']>({
        method: 'GET',
        path: `${base(ledgerId)}/account-flow-summaries`,
        query: projectQuery(query, ACCOUNT_FLOW_SUMMARIES_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return {
        displayCurrency: response.displayCurrency,
        accounts: response.accounts.map((account) => ({
          accountId: account.accountId,
          count: account.count,
          inflow: fromApiMoney(account.inflow),
          outflow: fromApiMoney(account.outflow),
          transfersIn: fromApiMoney(account.transfersIn),
          transfersOut: fromApiMoney(account.transfersOut),
          net: fromApiMoney(account.net),
          adjustments: fromApiMoney(account.adjustments),
        })),
      }
    },

    async lentTo(ledgerId, query, signal) {
      const response = await client.call<Schemas['LentToResponse']>({
        method: 'GET',
        path: `${base(ledgerId)}/lent-to`,
        query: projectQuery(query, LENT_TO_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return {
        displayCurrency: response.displayCurrency,
        items: response.items.map(toLentToItem),
      }
    },
  }
}

function toLentToItem(item: Schemas['LentToItemResponse']): LentToItem {
  const target: LentToTarget =
    item.contactId !== undefined
      ? { kind: 'contact', contactId: item.contactId }
      : { kind: 'ledger', otherLedgerId: item.otherLedgerId! }
  return {
    target,
    name: item.name,
    owed: fromApiMoney(item.owed),
    repaid: fromApiMoney(item.repaid),
    forgiven: fromApiMoney(item.forgiven),
  }
}

function toCashFlowTotals(totals: Schemas['CashFlowTotalsResponse']): CashFlowTotals {
  return {
    income: fromApiMoney(totals.income),
    expense: fromApiMoney(totals.expense),
    net: fromApiMoney(totals.net),
    savingsRate: totals.savingsRate === undefined ? undefined : ratio(totals.savingsRate),
  }
}

function toExpenseGroup(
  item: Schemas['ExpenseBreakdownItemResponse'],
  groupBy: ExpenseGroupBy,
): ExpenseGroup {
  if (groupBy === 'PAYEE') return { kind: 'payee', merchantId: item.merchantId, name: item.payee }
  if (item.accountId !== undefined) return { kind: 'category', accountId: item.accountId }
  if (item.tag !== undefined) return { kind: 'tag', tag: item.tag }
  return { kind: 'untagged' }
}

function toExpenseBreakdownItem(
  item: Schemas['ExpenseBreakdownItemResponse'],
  groupBy: ExpenseGroupBy,
): ExpenseBreakdownItem {
  return {
    group: toExpenseGroup(item, groupBy),
    amount: fromApiMoney(item.amount),
    entryCount: item.entryCount,
    share: item.share === undefined ? undefined : ratio(item.share),
  }
}

function toNetWorthPoint(point: Schemas['NetWorthPointResponse']): NetWorthPoint {
  return {
    date: parseLocalDate(point.date),
    cash: fromApiMoney(point.cash),
    netWorth: fromApiMoney(point.netWorth),
  }
}

function toParentRollupPeriod(period: Schemas['ParentRollupPeriodResponse']): ParentRollupPeriod {
  return {
    periodStart: parseLocalDate(period.periodStart),
    otherByParent: new Map(
      Object.entries(period.otherByParent).map(([parentId, amount]) => [parentId, fromApiMoney(amount)]),
    ),
    rows: period.rows.map((row) => ({
      accountId: row.accountId,
      parentId: row.parentId,
      ownBalance: fromApiMoney(row.ownBalance),
      rolledUpBalance: fromApiMoney(row.rolledUpBalance),
    })),
  }
}

function toReportPeriod(period: Schemas['ReportPeriodResponse']): ReportPeriod {
  const byAccount: Record<string, ReportCell> = {}
  for (const [accountId, cell] of Object.entries(period.byAccount)) {
    byAccount[accountId] = {
      amount: fromApiMoney(cell.amount),
      entryCount: cell.entryCount,
      isLowVolume: cell.isLowVolume,
    }
  }
  return {
    periodStart: parseLocalDate(period.periodStart),
    periodTotal: fromApiMoney(period.periodTotal),
    byAccount,
  }
}

function toDashboard(response: Schemas['DashboardResponse']): Dashboard {
  return {
    asOf: parseLocalDate(response.asOf),
    displayCurrency: response.displayCurrency,
    cash: fromApiMoney(response.cash),
    netWorth: fromApiMoney(response.netWorth),
    totalLent: fromApiMoney(response.totalLent),
    totalBorrowed: fromApiMoney(response.totalBorrowed),
    incomeMonthToDate: fromApiMoney(response.incomeMonthToDate),
    expenseMonthToDate: fromApiMoney(response.expenseMonthToDate),
    netWorthSeries: response.netWorthSeries.map(toNetWorthPoint),
    netWorthSeriesResolutionDays: response.netWorthSeriesResolutionDays,
    weeklySpending: response.weeklySpending.map((week) => ({
      weekStart: parseLocalDate(week.weekStart),
      amount: fromApiMoney(week.amount),
    })),
    cashFlow: {
      grow: fromApiMoney(response.cashFlow.grow),
      owe: fromApiMoney(response.cashFlow.owe),
      live: fromApiMoney(response.cashFlow.live),
      surplus: fromApiMoney(response.cashFlow.surplus),
    },
  }
}
